import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DATASET_TRIGGER_RUN_MODES,
  DATASET_TRIGGER_RUN_MODE_DESCRIPTIONS,
  DATASET_TRIGGER_RUN_MODE_LABELS,
  PersistedWorkflow,
  isDatasetTriggerRunMode,
  type DatasetTriggerRunMode,
} from '@cotera/sdk/core';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { Text, toast } from '@cotera/client/app/components/ui';
import { ResourceType } from '@cotera/api';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import type { WorkflowDatasetTriggerViewData } from '../types';
import { Field } from '../../node/edit-strategies/editor-field';
import { workflowDatasetViewEntry } from '../../../workflow-artifact-views';
import { useOpenWorkflowResource } from '../../../use-open-workflow-resource';
import { useWorkflowGraphMutations } from '../../../use-workflow-graph-mutations';
import { useWorkflowModel } from '../../../workflow.model';
import { draftUpdateDatasetTrigger } from '../../../workflow-draft-graph';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

/**
 * The inspector surface for a dataset trigger.
 *
 * Most of what "edit" means for a dataset trigger — which rows it watches, what
 * it fires on — lives on the dataset page, so this offers an explicit "Open
 * dataset" button through to it. Run mode is the exception: it is a one-choice
 * toggle rather than a form, and leaving it uneditable here is what stranded
 * manual-only triggers (no surface could switch them back to automatic), so it
 * is editable in place. `bindingId` is optional so a trigger still mid-add
 * (no binding yet) falls back to the read-only view.
 */
export const DatasetTriggerEditor: React.FC<{
  workflowId: string;
  bindingId?: string;
  trigger: WorkflowDatasetTriggerViewData;
  onOpened?: () => void;
}> = ({ workflowId, bindingId, trigger, onOpened }) => {
  const openWorkflowResource = useOpenWorkflowResource();
  const model = useWorkflowModel();
  const mutations = useWorkflowGraphMutations();
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);

  // Optimistic: reflect the pick immediately, reconcile if the save fails.
  const [runMode, setRunMode] = useState<DatasetTriggerRunMode>(
    trigger.runMode
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRunMode(trigger.runMode);
  }, [trigger.id, trigger.runMode]);

  const openDataset = (): void => {
    // A dataset trigger without a dataset cannot exist — the wizard and the API
    // both require one — so an empty id means the graph is corrupt, and there is
    // nothing to open.
    if (trigger.datasetId === '') {
      return;
    }
    openWorkflowResource({
      resourceType: ResourceType.Dataset,
      artifactId: trigger.datasetId,
      fallbackEntry: workflowDatasetViewEntry({
        workflowId,
        datasetId: trigger.datasetId,
        datasetName: trigger.name,
        fallbackTitle:
          trigger.name.trim() !== '' ? trigger.name : 'Dataset change',
      }),
    });
    onOpened?.();
  };

  const isPersistedOnServer = model
    .getBaselineDraftState()
    .graph.triggers.some(
      (snapshot) =>
        snapshot.trigger.id === trigger.id &&
        snapshot.kind === PersistedWorkflowTriggerKind.Dataset
    );

  const handleRunModeChange = async (
    next: DatasetTriggerRunMode
  ): Promise<void> => {
    if (next === runMode || saving || bindingId === undefined) {
      return;
    }
    const previous = runMode;
    setRunMode(next);
    setSaving(true);

    const patch = { bindingId, runMode: next };

    if (!isPersistedOnServer) {
      const saved = await mutations.updateDatasetTrigger(patch);
      setSaving(false);
      if (!saved) {
        setRunMode(previous);
        return;
      }
      toast.success('Run mode updated');
      return;
    }

    const result = await client.resource.workflow.updateTrigger({
      workflowId,
      bindingId,
      triggerId: trigger.id,
      runMode: next,
    });
    if (result.isErr()) {
      setSaving(false);
      setRunMode(previous);
      toast.error(
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to update run mode'
      );
      return;
    }

    await mutations.updateDatasetTrigger(patch);
    // Keep baseline in sync so an immediate API save doesn't look like an
    // unsaved graph edit (and doesn't require a full rehydrate that would wipe
    // other draft changes).
    model.updateBaselineDraftState((state) =>
      draftUpdateDatasetTrigger(state, patch)
    );
    void queryClient.invalidateQueries({
      queryKey: queryKeys.resource.get({
        orgId,
        type: ResourceType.Workflow,
        id: workflowId,
      }),
    });
    setSaving(false);
    toast.success('Run mode updated');
  };

  return (
    <div className="space-y-4">
      <Text.Caption className="text-muted-foreground block">
        This workflow runs when the dataset changes. Open the dataset to see the
        rows it watches and edit what it fires on.
      </Text.Caption>

      {bindingId !== undefined ? (
        <Field
          label="Run this workflow"
          hint={DATASET_TRIGGER_RUN_MODE_DESCRIPTIONS[runMode]}
        >
          <Select
            value={runMode}
            disabled={saving}
            onValueChange={(next) => {
              if (isDatasetTriggerRunMode(next)) {
                void handleRunModeChange(next);
              }
            }}
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATASET_TRIGGER_RUN_MODES.map((mode) => (
                <SelectItem key={mode} value={mode}>
                  {DATASET_TRIGGER_RUN_MODE_LABELS[mode]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      ) : null}

      <Button
        type="button"
        variant="outline"
        className="w-full gap-2"
        onClick={openDataset}
      >
        <ExternalLink className="h-4 w-4" />
        Open dataset
      </Button>
    </div>
  );
};
